/* Sampling — moved out of pages/Sampling.jsx in split step 6d (28-Oct-2026), unchanged. */
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { fmtDateTimeFirm } from "@/lib/dt";   // 5c · F-5
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)

// ------------------------------------------------------------------
// Continuous (100% Population) rule-selection panel. Reuses fraud +
// analytics detections — never re-scans.
// ------------------------------------------------------------------
function ContinuousPanel({ rules, selected, onToggle, scope, onScope,
                            thresholdAmount, onThreshold, scan }) {
  const { t, lang, dict } = useI18n();
  const [presets, setPresets] = useState([]);
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  const reloadPresets = async () => {
    try {
      const r = await api.get("/samples/continuous-presets");
      setPresets(r.data || []);
    } catch { setPresets([]); }
    setPresetsLoaded(true);
  };
  useEffect(() => { reloadPresets(); }, []);

  const applyPreset = (p) => {
    // Fill selection + scope + threshold from the preset. The parent owns
    // `selected`/`scope`/`thresholdAmount` state — walk the toggle callback
    // to make sure the parent's Set is populated deterministically.
    const target = new Set(p.rules || []);
    for (const r of rules) {
      const inTarget = target.has(r.key);
      const nowIn = selected.has(r.key);
      if (inTarget !== nowIn) onToggle(r.key);
    }
    if (p.scope) onScope(p.scope === "individual" ? "individual" : "combined");
    if (p.threshold_amount != null) onThreshold(String(p.threshold_amount));
    toast.success(t("sampling.toasts.loadedPreset").replace("{name}", p.name));
  };

  const saveCurrentAsPreset = async () => {
    if (!saveName.trim()) { toast.error(t("sampling.toasts.givePresetName")); return; }
    if (selected.size === 0) { toast.error(t("sampling.toasts.pickRuleFirst")); return; }
    setSaving(true);
    try {
      const body = {
        name: saveName.trim(),
        rules: Array.from(selected),
        scope: selected.size > 1 ? scope : "combined",
        threshold_amount: selected.has("Threshold breach") && thresholdAmount
          ? Number(thresholdAmount) : null,
      };
      await api.post("/samples/continuous-presets", body);
      toast.success(t("sampling.toasts.presetSaved").replace("{name}", body.name));
      setSaveOpen(false); setSaveName("");
      await reloadPresets();
    } catch (e) {
      toast.error(toErrMsg(e, t("sampling.toasts.savePresetFailed")));
    } finally { setSaving(false); }
  };

  const deletePreset = async (p) => {
    if (!await askConfirm(t("sampling.toasts.deletePresetConfirm").replace("{name}", p.name))) return;
    try {
      await api.delete(`/samples/continuous-presets/${p.id}`);
      toast.success(t("sampling.toasts.presetDeleted"));
      await reloadPresets();
    } catch (e) {
      toast.error(toErrMsg(e, t("sampling.toasts.deleteFailed")));
    }
  };

  const grouped = {
    "Fraud Risk (ISA 240)": rules.filter((r) => r.source !== "Analytics"),
    "Analytics": rules.filter((r) => r.source === "Analytics"),
  };
  const totalSelected = selected.size;
  const totalFlagged = rules
    .filter((r) => selected.has(r.key))
    .reduce((sum, r) => sum + (r.count || 0), 0);
  return (
    <div className="md:col-span-4 rounded-md border bg-emerald-50/40 p-3 dark:bg-emerald-950/20"
          data-testid="continuous-panel">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <ShieldAlert className="h-4 w-4 text-emerald-600" />
          {t("sampling.continuousRulesTitle")}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {totalSelected === 0 ? t("sampling.noRuleSelected")
            : t("sampling.selectedSuffix").replace("{rules}", fmtPlural(totalSelected, lang, dict.plurals.rules)).replace("{tx}", fmtPlural(totalFlagged, lang, dict.plurals.transactions))}
        </div>
      </div>

      {/* Presets — save a rule combo once, reuse across engagements */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded border bg-white p-2 text-xs dark:bg-slate-950"
            data-testid="continuous-presets-row">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("sampling.presets")}</span>
        {presetsLoaded && presets.length === 0 && (
          <span className="text-[10px] italic text-muted-foreground">
            {t("sampling.noPresets")}
          </span>
        )}
        {presets.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1 rounded-md border bg-slate-50 px-2 py-0.5 dark:bg-slate-900"
                 data-testid={`continuous-preset-${p.id}`}>
            <button type="button" onClick={() => applyPreset(p)}
                       className="text-[11px] font-medium text-emerald-700 hover:underline"
                       title={t("sampling.presetTooltip")
                         .replace("{rules}", fmtPlural(p.rules.length, lang, dict.plurals.rules))
                         .replace("{scope}", p.scope)
                         .replace("{threshold}", p.threshold_amount ? t("sampling.presetThresholdSuffix").replace("{n}", p.threshold_amount) : "")}
                       data-testid={`continuous-preset-apply-${p.id}`}>
              {p.name}
            </button>
            <button type="button" onClick={() => deletePreset(p)}
                       title={t("sampling.deletePresetTitle")}
                       className="text-[10px] text-red-500 hover:text-red-700"
                       data-testid={`continuous-preset-delete-${p.id}`}>×</button>
          </span>
        ))}
        <div className="ms-auto">
          {saveOpen ? (
            <div className="flex items-center gap-1">
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                       placeholder={t("sampling.presetNamePh")}
                       autoFocus
                       className="h-7 w-56 text-xs"
                       onKeyDown={(e) => e.key === "Enter" && saveCurrentAsPreset()}
                       data-testid="continuous-preset-name-input" />
              <Button size="sm" onClick={saveCurrentAsPreset} disabled={saving}
                          className="h-7 text-xs"
                          data-testid="continuous-preset-save-confirm">
                {saving ? "…" : t("sampling.saveBtn")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setSaveOpen(false); setSaveName(""); }}
                          className="h-7 text-xs">{t("sampling.cancelBtn")}</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline"
                       onClick={() => setSaveOpen(true)}
                       disabled={selected.size === 0}
                       className="h-7 text-xs"
                       data-testid="continuous-preset-save-open">
              {t("sampling.saveAsPreset")}
            </Button>
          )}
        </div>
      </div>

      {Object.entries(grouped).map(([source, list]) => (
        <div key={source} className="mb-3">
          <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("sampling.fromSource").replace("{source}", source === "Fraud Risk (ISA 240)" ? t("sampling.fraudSource") : t("sampling.analyticsSource"))}
          </div>
          <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
            {list.map((r) => (
              <label key={r.key}
                        className={`flex cursor-pointer items-center gap-2 rounded border bg-white px-2 py-1.5 text-xs hover:border-emerald-400 dark:bg-slate-950 ${
                          selected.has(r.key) ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40" : ""
                        }`}
                        data-testid={`continuous-rule-${r.key}`}>
                <Checkbox checked={selected.has(r.key)}
                             onCheckedChange={() => onToggle(r.key)}
                             data-testid={`continuous-rule-toggle-${r.key}`} />
                <span className="flex-1">{r.key}</span>
                <span className={`rounded-md px-1.5 py-0.5 text-[10px] ${
                  r.count > 0
                    ? "bg-amber-100 text-amber-800"
                    : "bg-slate-100 text-slate-500"
                }`} data-testid={`continuous-rule-count-${r.key}`}>{r.count === null || r.count === undefined ? "—" : r.count}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      {selected.has("Threshold breach") && (
        <div className="mb-3 flex items-center gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs dark:bg-amber-950/30"
              data-testid="continuous-threshold-row">
          <Label className="text-xs">{t("sampling.thresholdAmount")}</Label>
          <Input type="number" step="0.01" min="0"
                   value={thresholdAmount}
                   onChange={(e) => onThreshold(e.target.value)}
                   placeholder={t("sampling.thresholdPh")}
                   className="h-7 w-40 text-xs"
                   data-testid="continuous-threshold-input" />
          <span className="text-[10px] text-muted-foreground">
            {t("sampling.thresholdHelp")}
          </span>
        </div>
      )}

      {selected.size > 1 && (
        <div className="mt-1 rounded border bg-white p-2 text-xs dark:bg-slate-950"
              data-testid="continuous-scope">
          <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("sampling.sampleScope")}
          </div>
          <RadioGroup value={scope} onValueChange={onScope}
                         className="flex flex-col gap-1">
            <label className="flex cursor-pointer items-start gap-2"
                      data-testid="continuous-scope-combined">
              <RadioGroupItem value="combined" className="mt-0.5" />
              <div>
                <div className="font-medium">{t("sampling.combinedScope")}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t("sampling.combinedHelp")}
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-2"
                      data-testid="continuous-scope-individual">
              <RadioGroupItem value="individual" className="mt-0.5" />
              <div>
                <div className="font-medium">{t("sampling.individualScope")}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t("sampling.individualHelp")}
                </div>
              </div>
            </label>
          </RadioGroup>
        </div>
      )}

      {/* Batch 9 · A20 (F-73): one engine — the nine journal-entry rules read the latest A9001 scan; the scan time is shown,
          and a missing scan is said plainly */}
      <div className={`mt-2 rounded border p-2 text-[10px] ${scan && !scan.scan_present ? "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" : "text-muted-foreground"}`} data-testid="continuous-scan-note">
        {scan && scan.scan_present
          ? (lang === "ar"
              ? `أعداد قيود اليومية (J1–J13) من آخر مسح لاختبار قيود اليومية بتاريخ ${fmtDateTimeFirm(scan.scan_at)} — نفس المحرك ونفس المعاملات (عطلة الارتباط، العطلات، الساعات، قائمة المعتمدين، المستخدمين، التافه بوضوح من 105(a)). أعداد التحليلات الثلاثة تُحسب هنا.`
              : `Journal-entry rule counts (J1–J13) are those of the latest Journal entry testing scan of ${fmtDateTimeFirm(scan.scan_at)} — the same engine and the same parameters (the engagement weekend, holidays, hours, the approver list, the users, clearly trivial from 105(a)). The three Analytics counts are computed here.`)
          : (scan && scan.no_scan_message) || t("sampling.populationFootnote")}
      </div>
    </div>
  );
}

export { ContinuousPanel };
